// FinanceApprovals — encodes FA.PL.001 §5.5 purchase-approval thresholds and the
// §5.4.6 approval-authority registry (Treasurer maintains it). Board/staff.

import { useAuthority, saveAuthority } from "../hooks/useFinance";
import type { AuthorityRow } from "../hooks/useFinance";
import { useSaveStatus } from "../hooks/useSaveStatus";
import ChartCard from "../components/dashboard/ChartCard";
import PageIntro from "../components/shared/PageIntro";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import { TH, FetchErrorBanner } from "../components/finance/ui";

// Non-overlapping bands — the boundary dollar belongs to exactly one row.
const INSIDE = [
  { band: "Under $1,000", who: "Primary OR Secondary authority" },
  { band: "$1,000 – $4,999", who: "Primary AND Secondary authority" },
  { band: "$5,000 and up", who: "Board of Directors" },
];
const OUTSIDE = [
  { band: "Under $1,000", who: "Primary OR Secondary authority" },
  { band: "$1,000 and up", who: "Board of Directors" },
];

export default function FinanceApprovals() {
  const { data, isLoading, refresh, error } = useAuthority();
  const authorities = data?.authorities ?? [];

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title="Approvals & Authorities"
          subtitle="Purchase-approval thresholds (FA.PL.001 §5.5) and the approval-authority registry the Treasurer maintains (§5.4.6)."
        />
        <PageIntro>
          Who can approve a purchase at each dollar amount — so you know what
          needs a board vote and what doesn't.
        </PageIntro>
      </div>

      <FetchErrorBanner error={error} what="the approval registry" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard
          title="Budgeted Items — Approval Thresholds"
          subtitle="§5.5.3 · purchases within an approved budget"
        >
          <table className="w-full text-sm">
            <caption className="sr-only">
              Budgeted-item approval thresholds
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle text-theme-muted text-xs uppercase tracking-wider">
                <th scope="col" className="text-left py-2">
                  Amount
                </th>
                <th scope="col" className="text-left py-2">
                  Approval Required
                </th>
              </tr>
            </thead>
            <tbody>
              {INSIDE.map((r) => (
                <tr
                  key={r.band}
                  className="hover:bg-theme-overlay/50 transition-colors"
                >
                  <td className="py-2 text-theme-primary font-medium tabular-nums">
                    {r.band}
                  </td>
                  <td className="py-2 text-theme-secondary">{r.who}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartCard>
        <ChartCard
          title="Non-Budgeted Items — Approval Thresholds"
          subtitle="§5.5.4 · purchases not in an approved budget"
        >
          <table className="w-full text-sm">
            <caption className="sr-only">
              Non-budgeted-item approval thresholds
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle text-theme-muted text-xs uppercase tracking-wider">
                <th scope="col" className="text-left py-2">
                  Amount
                </th>
                <th scope="col" className="text-left py-2">
                  Approval Required
                </th>
              </tr>
            </thead>
            <tbody>
              {OUTSIDE.map((r) => (
                <tr
                  key={r.band}
                  className="hover:bg-theme-overlay/50 transition-colors"
                >
                  <td className="py-2 text-theme-primary font-medium tabular-nums">
                    {r.band}
                  </td>
                  <td className="py-2 text-theme-secondary">{r.who}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartCard>
      </div>

      <ChartCard
        title="Approval-Authority Registry"
        subtitle="One primary + one secondary per owner; nobody holds both for the same owner (§5.4.7). Edits auto-save."
        empty={!isLoading && authorities.length === 0}
        minHeight={120}
      >
        <div className="overflow-x-auto -mx-4 md:-mx-5">
          <table className="w-full min-w-[640px] text-left">
            <caption className="sr-only">
              Approval-authority registry by committee
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  Committee / Division / Office
                </th>
                <th scope="col" className={TH}>
                  Primary Authority
                </th>
                <th scope="col" className={TH}>
                  Secondary Authority
                </th>
              </tr>
            </thead>
            <tbody>
              {authorities.map((a) => (
                <AuthorityRowEl key={a.committee} a={a} onSaved={refresh} />
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
      <p className="text-xs text-theme-muted">
        Authorities must be Board members or named officers (§5.4.3). The
        Treasurer keeps this current (§5.4.6). When a purchase exceeds the
        threshold for its band, it routes to the Board. How a purchase is paid
        doesn't change the approval rule — a non-budgeted purchase of $1,000 or
        more needs Board approval (§5.5.4).
      </p>
    </PageShell>
  );
}

function AuthorityRowEl({
  a,
  onSaved,
}: {
  a: AuthorityRow;
  onSaved: () => void;
}) {
  const status = useSaveStatus();
  async function save(patch: {
    primary_authority?: string;
    secondary_authority?: string;
  }) {
    const okSaved = await status.run(
      () => saveAuthority(a.committee, patch),
      "save authority",
    );
    if (okSaved) onSaved();
  }
  return (
    <tr className="hover:bg-theme-overlay/50 transition-colors">
      <td className="px-3 py-2 text-sm font-medium text-theme-primary">
        {a.committee}
        {status.error && (
          <div className="text-[11px] text-status-red mt-0.5">
            {status.error}
          </div>
        )}
        {status.saved && !status.error && (
          <div className="text-[11px] text-status-green mt-0.5">Saved ✓</div>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          defaultValue={a.primary_authority ?? ""}
          placeholder="Name…"
          onBlur={(e) => {
            if (e.target.value !== (a.primary_authority ?? ""))
              save({ primary_authority: e.target.value });
          }}
          className="w-full min-w-[160px] px-2 py-1 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none"
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="text"
          defaultValue={a.secondary_authority ?? ""}
          placeholder="Name…"
          onBlur={(e) => {
            if (e.target.value !== (a.secondary_authority ?? ""))
              save({ secondary_authority: e.target.value });
          }}
          className="w-full min-w-[160px] px-2 py-1 text-sm rounded-md bg-theme-surface border border-theme-subtle focus:border-dcvfd-accent focus:outline-none"
        />
      </td>
    </tr>
  );
}
